/**
 * The only table in the application (§4.3): TanStack Table behind the shadcn table primitive.
 *
 * It renders a result; it does not filter, group, pivot, aggregate or interpret values. It DOES
 * sort (§4.3, DECISIONS.md §30, owner decision 2026-08-06): a sortable header re-orders the rows
 * already loaded and never re-queries, because sort left the request body. The order still travels
 * in the URL, so a shared link reproduces it.
 * Paging is likewise local, because a page is a viewport over a response rather than a different
 * answer: page position stays out of the URL (§4.4), the totals row sums the whole response rather
 * than the visible page, and the row count claims "of N" only when the server confirmed the
 * response was not truncated.
 * Sorting is the one client-side transform NOT gated on completeness — the owner accepted that a
 * truncated result orders only the rows that came back. Do not extend that exemption to the totals
 * row, the row count, or option lists; they still gate.
 *
 * Why it is shaped the way it is: `src/components/design-notes.md`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faSort,
  faSortDown,
  faSortUp,
  faTableList,
} from '@fortawesome/free-solid-svg-icons';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type PaginationState,
  type RowData,
  type SortingState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCaption,
  TableFooter,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { SortSpec } from '@/lib/api';
import { LOCALE } from '@/lib/locale';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';

export type ColumnAlign = 'left' | 'center' | 'right';

declare module '@tanstack/react-table' {
  /**
   * Per-column presentation, declared in `columns.tsx` beside the report. Presentation only —
   * anything that decides what a number *means* belongs in SQL (§5.2).
   */
  // The type parameters are fixed by the interface being augmented and must match it exactly,
  // even though this augmentation has no use for them.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Measures right-align. Figures then line up digit under digit and scan far faster. */
    align?: ColumnAlign;
    /**
     * Align the heading differently from the cells under it. Defaults to {@link align}. Sort
     * direction still follows the cells: a column of figures opens worst-first either way.
     */
    headerAlign?: ColumnAlign;
    headerClassName?: string;
    cellClassName?: string;
    /**
     * Whether this column participates in the totals row. `'sum'` is the only computed option,
     * because a straight sum is the only aggregate §4.3 permits the client to derive; a ratio,
     * weighted average or percentage is left unset and supplied from SQL via `totals.values`.
     */
    total?: 'sum';
    /**
     * This column's heading in an export, and the band it sits under (§5.9). A leaf without
     * `exportLabel` is not exported at all — see `exportColumns` in `groupedColumns.tsx`.
     */
    exportLabel?: string;
    exportGroup?: string;
    /**
     * Merge this column's cell down over a run of adjacent rows carrying the same value — the
     * repeated identity of a block of rows, written once. For a grid whose rows come in blocks
     * (one supplier, four KPI rows), repeating the name four times is four times the ink for one
     * fact, and the block's extent is left for the reader to infer.
     *
     * Adjacent only, and computed over the rendered page, so it cannot merge two runs that a sort
     * separated, and a block split by a page boundary is re-labelled at the top of the next page
     * rather than continuing headless. Ignored under {@link DataTableProps.virtualized}: those
     * rows are a moving window, and a cell spanning out of it would be unmountable.
     *
     * The last row of each run also takes a heavier bottom rule. The merge alone says the name is
     * shared; the rule is what makes the block's end findable while scanning the figures to the
     * right of it.
     */
    spanRows?: boolean;
  }
}

/** What to render in the bottom totals row. Omit it and there is no totals row. */
export interface DataTableTotals {
  /** The column whose cell carries the label — the legacy puts "Total" under Factory. */
  labelColumn: string;
  label: React.ReactNode;
  /**
   * Server-computed totals by column id, for anything the client must not derive: percentages,
   * ratios, weighted averages (§4.3). Takes precedence over a client-side sum.
   */
  values?: Record<string, React.ReactNode>;
  /** Renders a client-computed sum, so the totals row formats like the column above it. */
  format: (value: number, columnId: string) => React.ReactNode;
}

/**
 * An extra summary row beneath the totals row, for a legacy screen that drew more than one — the
 * hourly monitor's "Perf to Plan % (Hour)", "Cumulative Output" and "Cumulative Perf. To Plan %".
 *
 * Every figure is supplied, never derived: there is no `total: 'sum'` path here on purpose. These
 * rows are ratios and running totals over the whole selection, which §4.3 keeps in SQL, so the
 * caller passes what the server returned and gates on a complete result exactly as it does for
 * `totals`.
 */
export interface DataTableFooterRow {
  /** Stable React key, and what the row is called if it has no visible label cell. */
  id: string;
  /** The column whose cell carries the label. */
  labelColumn: string;
  label: React.ReactNode;
  /** Server-computed values by column id. A column with no entry renders empty. */
  values: Record<string, React.ReactNode>;
}

export interface DataTableProps<TRow, TValue = unknown> {
  /** Column definitions, from `reports/<id>/columns.tsx`. */
  columns: ColumnDef<TRow, TValue>[];
  data: TRow[];
  isLoading?: boolean;
  /** Say what to do next, not just that there is nothing here (§4.10). */
  emptyMessage?: React.ReactNode;
  /** Required beyond roughly 1000 rows (§4.3). Adds a bounded scroll area. */
  virtualized?: boolean;
  /**
   * The order to put the rows in, read from the URL by `useReportSort` (§4.4). This table applies
   * it (DECISIONS.md §30) — pass unsorted rows and let it own the order rather than sorting them
   * first, or the two orderings fight and this one wins. Passing this without `onSortChange`
   * renders the indicator but no controls.
   */
  sort?: SortSpec[];
  /**
   * Called when a reader clicks a sortable header. It does not re-query: the page writes the new
   * sort to the URL, the URL comes back as {@link sort}, and the rows already loaded are reordered
   * here. A column must also declare `enableSorting: true` — see the header, which is where the
   * offered set is governed.
   */
  onSortChange?: (sort: SortSpec[]) => void;
  /**
   * A bottom totals row, as the legacy screen has. Pass it **only when the result is complete** —
   * `truncated === false` in the response envelope (§5.7). A total over a capped result sums part
   * of the data and presents it as the whole (§4.3).
   */
  totals?: DataTableTotals;
  /**
   * Further summary rows under the totals row, in the order given. Same completeness rule as
   * `totals` — pass them only over an untruncated result.
   */
  footerRows?: DataTableFooterRow[];
  /**
   * `truncated` from the response envelope (§5.7). A row count is itself a whole-result claim
   * (§4.3), so a truncated result makes the pager say "of 50,000 loaded" rather than "of 50,000"
   * and drops the "totals cover every row" note. It does not gate the pager itself.
   */
  truncated?: boolean;
  /** A stable row identity, if the data has one. Falls back to the row index. */
  getRowId?: (row: TRow, index: number) => string;
  /** A one-line description of the table, announced to screen readers and not shown. */
  caption?: string;
  /**
   * How the table is dressed. `grid` — the default: banded rows, a filled header, a rule between
   * every cell, for a report forty columns wide. `board` — a wall board of a few wide columns:
   * one hairline between rows, nothing else, stretched to its container so a column declaring
   * `w-full` in `cellClassName` absorbs the slack.
   */
  variant?: 'grid' | 'board';
  /**
   * Fill the height of the container and drop the pager — nobody walks up to a wall TV to click
   * Next. Only for results known to be small; the truncation notice above the table still fires
   * if the server capped the result (§4.3).
   */
  fitToScreen?: boolean;
  /** Show all rows without a pager while retaining the normal table height. For small summaries. */
  paginate?: boolean;
  /**
   * A viewport onto a complete result for a wall board whose rows cannot all fit, advanced by the
   * caller on a timer — the departure-board treatment. Only read alongside {@link fitToScreen}.
   *
   * Paging a complete result is not a partial result (§4.3): every row is in the browser and each
   * is shown in turn. The caller owns the rotation and must say on screen which page is up, or the
   * board silently implies the lines it is not currently showing do not exist.
   */
  page?: { index: number; size: number };
  className?: string;
}

/** Virtualizer row height: text-xs line (16px) + py-1.5 (12px) + the row border (1px). */
const ROW_HEIGHT = 29;
const SKELETON_ROWS = 8;
const PAGE_SIZES = [25, 50, 100, 250] as const;

/** Grouped digits: "1–25 of 50000" is misread at a glance in a way "1–25 of 50,000" is not. */
const count = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });

const alignClass: Record<ColumnAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  // `tabular-nums` keeps digits on a fixed pitch so columns of figures line up as you scan down.
  right: 'text-right tabular-nums',
};

/**
 * Header titles wrap rather than run their column wide. `max-width` on a table cell is ignored
 * under `table-layout: auto`, so the bound must sit on an element *inside* the cell — which is why
 * a plain-string title is rendered into a bounded `inline-block` below. Exported so a report that
 * renders its own header element uses the same measurement.
 */
export const WRAP_HEADER = 'whitespace-normal break-words [line-height:1.2]';
export const HEADER_TITLE = `inline-block max-w-24 ${WRAP_HEADER}`;

export function DataTable<TRow, TValue>({
  columns,
  data,
  isLoading = false,
  emptyMessage = 'No rows match these filters. Widen the date range or clear a filter to see more.',
  virtualized = false,
  sort,
  onSortChange,
  totals,
  footerRows,
  truncated = false,
  getRowId,
  caption,
  variant = 'grid',
  fitToScreen = false,
  paginate = true,
  page,
  className,
}: DataTableProps<TRow, TValue>) {
  const { text } = useI18n();
  const isBoard = variant === 'board';
  const scrollRef = useRef<HTMLDivElement>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZES[0],
  });
  // A board shows the whole result at once; kept as a page size so the reset-to-page-1, the row
  // range and the totals gate all keep working with no branch of their own. A board too tall for
  // its screen passes `page` and shows one slice at a time instead.
  const effectivePagination = fitToScreen || !paginate
    ? {
        pageIndex: page === undefined ? 0 : page.index,
        pageSize: page === undefined ? Math.max(data.length, 1) : Math.max(page.size, 1),
      }
    : pagination;
  // The URL's sort, in TanStack's shape. Derived rather than stored, so the URL stays the one place
  // the order lives and the two cannot drift apart.
  //
  // **The memo is load-bearing, not a micro-optimisation.** `getSortedRowModel` memoises on this
  // array by reference, so rebuilding it every render re-sorts every render and hands
  // `useReactTable` — which mutates its instance during render — a different result each pass. A
  // render that never settles never commits, and react-router runs every location change inside
  // `startTransition`: the address bar moved and the page did not, on every report at once.
  const sorting = useMemo<SortingState>(
    () => (sort ?? []).map((spec) => ({ id: spec.field, desc: spec.dir === 'desc' })),
    [sort],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // The browser sorts (§4.3, DECISIONS.md §30). `sort` still travels in the URL, so a shared link
    // reproduces the order; what it no longer does is re-query, since sort left the request body.
    manualSorting: false,
    getSortedRowModel: getSortedRowModel(),
    // Filtering is NOT the same call: sorting re-orders an answer, filtering changes which rows are
    // in it, so anything the server filtered stays filtered there.
    manualFiltering: true,
    // Paging is a viewport over an answer already in the browser, not a narrowing of it (§4.3);
    // leaving `manualPagination` off is also what resets to page 1 when `data` changes.
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    // Driven from the URL-backed `sort` prop rather than held here: the order is part of the answer
    // (§4.4), so it must survive a shared link. `<DataTable>` applies it; it does not own it.
    state: { pagination: effectivePagination, sorting },
    ...(getRowId !== undefined
      ? { getRowId: (row: TRow, index: number) => getRowId(row, index) }
      : {}),
  });

  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length;

  // A new page begins at its first row, including where the wrapper owns the vertical scroll.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [effectivePagination.pageIndex, effectivePagination.pageSize]);

  const virtualizer = useVirtualizer({
    count: virtualized ? rows.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  const virtualRows = virtualized ? virtualizer.getVirtualItems() : [];
  const first = virtualRows[0];
  const last = virtualRows[virtualRows.length - 1];
  const padTop = first === undefined ? 0 : first.start;
  const padBottom = last === undefined ? 0 : virtualizer.getTotalSize() - last.end;

  const toggleSort = (field: string, preferDescending: boolean) => {
    if (onSortChange === undefined) {
      return;
    }
    const current = sort?.find((spec) => spec.field === field);
    if (current === undefined) {
      onSortChange([{ field, dir: preferDescending ? 'desc' : 'asc' }]);
      return;
    }
    onSortChange([{ field, dir: current.dir === 'asc' ? 'desc' : 'asc' }]);
  };

  /**
   * Per rendered row, per `spanRows` column: how many rows this cell covers, or 0 for a row the
   * run above already covers — plus the rows that close a run, which take the block rule. Both are
   * empty unless a column asks for it, so nothing changes for the tables that do not.
   */
  const { rowSpans, blockEnds } = useMemo(() => {
    const spanning = table
      .getVisibleLeafColumns()
      .filter((column) => column.columnDef.meta?.spanRows === true)
      .map((column) => column.id);
    if (spanning.length === 0 || virtualized) {
      return { rowSpans: new Map<string, Map<string, number>>(), blockEnds: new Set<string>() };
    }

    const out = new Map<string, Map<string, number>>();
    const ends = new Set<string>();
    for (const columnId of spanning) {
      let runStart = 0;
      while (runStart < rows.length) {
        const value = rows[runStart]?.getValue(columnId);
        let end = runStart + 1;
        // A null never merges with the null below it: two blanks are two unknowns, not one fact.
        while (
          value !== null
          && value !== undefined
          && end < rows.length
          && Object.is(rows[end]?.getValue(columnId), value)
        ) {
          end += 1;
        }
        for (let index = runStart; index < end; index += 1) {
          const id = rows[index]?.id;
          if (id === undefined) continue;
          const byColumn = out.get(id) ?? new Map<string, number>();
          byColumn.set(columnId, index === runStart ? end - runStart : 0);
          out.set(id, byColumn);
        }
        // Not on the last row of the page: that edge is the table's own, and a block rule there
        // would claim the block ends where the page does.
        const closing = rows[end - 1]?.id;
        if (closing !== undefined && end < rows.length) {
          ends.add(closing);
        }
        runStart = end;
      }
    }
    return { rowSpans: out, blockEnds: ends };
  }, [rows, table, virtualized]);

  const renderCells = (row: (typeof rows)[number]) =>
    row.getVisibleCells().flatMap((cell) => {
      const meta = cell.column.columnDef.meta;
      const span = rowSpans.get(row.id)?.get(cell.column.id);
      // Covered by the run above: the cell is not rendered at all, or it would push the row along.
      if (span === 0) {
        return [];
      }
      // A spanning cell reaches the end of its own block, so its bottom edge is the boundary too —
      // it sits in the block's first row and would otherwise never see the closing row's rule.
      const closesBlock = blockEnds.has(row.id) || (span !== undefined && span > 1);
      return [
        <TableCell
          key={cell.id}
          rowSpan={span === undefined || span <= 1 ? undefined : span}
          className={cn(
            // A rule between every cell: on a forty-column table an unruled row is one smear.
            isBoard ? 'border-b border-border/60' : 'border-r border-table-cell-border',
            alignClass[meta?.align ?? 'left'],
            // The name sits against the block it names, not against its first row.
            span !== undefined && span > 1 && 'align-middle',
            closesBlock && 'border-b-2 border-b-table-header',
            meta?.cellClassName,
          )}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>,
      ];
    });

  /**
   * The totals row — §4.3, and only ever over a complete result; the caller suppresses `totals`
   * when the response was capped. It sums `data`, **not the visible page**, since a per-page
   * subtotal labelled "Total" is the believed-and-wrong number §4.3 exists to prevent. Straight
   * sums only; a ratio or percentage arrives from SQL via `totals.values`.
   */
  const totalsFor = (columnId: string): React.ReactNode => {
    if (totals === undefined) return null;
    if (columnId === totals.labelColumn) return totals.label;

    const supplied = totals.values?.[columnId];
    if (supplied !== undefined) return supplied;

    const column = table.getColumn(columnId);
    const meta = column?.columnDef.meta;
    if (meta?.total !== 'sum') return null;

    let sum = 0;
    let seen = false;
    for (const row of data) {
      const raw = (row as Record<string, unknown>)[columnId];
      if (raw === null || raw === undefined || raw === '') continue;
      const n = Number(raw);
      if (Number.isNaN(n)) continue;
      sum += n;
      seen = true;
    }
    return seen ? totals.format(sum, columnId) : null;
  };

  const pageCount = table.getPageCount();
  const firstRow =
    data.length === 0 ? 0 : effectivePagination.pageIndex * effectivePagination.pageSize + 1;
  const lastRow = Math.min(
    (effectivePagination.pageIndex + 1) * effectivePagination.pageSize,
    data.length,
  );
  const multiPage = pageCount > 1;

  return (
    // A definite height here, or the scroll box's `h-full` resolves against `auto` and a board
    // stacks at the top of the panel — the thing `fitToScreen` exists to prevent.
    <div className={cn('flex w-full flex-col gap-2', fitToScreen && 'min-h-0 flex-1')}>
      <div
        ref={scrollRef}
        className={cn(
          'w-full overflow-x-auto rounded-lg border bg-card',
          // Exactly one scroll box when virtualized — the sticky header and the row offsets only
          // agree if one element scrolls both ways — so the primitive's own container is neutralised.
          virtualized &&
            'max-h-192 overflow-y-auto [&>[data-slot=table-container]]:overflow-x-visible',
          // The inner container must be full height too, or the table's h-full resolves against nothing.
          fitToScreen && 'h-full max-h-none [&>[data-slot=table-container]]:h-full',
          isBoard && '[&>[data-slot=table-container]]:overflow-x-visible',
          className,
        )}
      >
        {/* w-full beats the primitive's w-max through twMerge; h-full spreads the slack over the rows. */}
        <Table className={cn(isBoard && 'w-full', fitToScreen && 'h-full')}>
        {caption !== undefined ? <TableCaption className="sr-only">{caption}</TableCaption> : null}

        {/* Sticky the whole <thead>, not each <th> at top-0: with per-cell sticky the second header
            row stacks on the first and row-spanned identity columns slide under the leaf headers. */}
        <TableHeader
          className={cn(
            'sticky top-0 z-20 [&_tr]:border-b-0',
            isBoard ? 'bg-background' : 'bg-table-header',
          )}
        >
          {table.getHeaderGroups().map((headerGroup, headerGroupIndex) => (
            <TableRow key={headerGroup.id} className="hover:bg-transparent">
              {headerGroup.headers.map((header) => {
                // Mixed flat + grouped columns; skipping TanStack's root-leaf placeholders shifts
                // every group heading left. See design-notes.md.
                const isRootLeaf =
                  header.column.parent === undefined && header.column.columns.length === 0;
                const headerRowCount = table.getHeaderGroups().length;

                if (isRootLeaf && header.isPlaceholder && headerGroupIndex === 0) {
                  // Fall through and render the label with full rowSpan below.
                } else if (isRootLeaf && !header.isPlaceholder && headerGroupIndex > 0) {
                  return null;
                } else if (header.isPlaceholder) {
                  // Nested placeholders still need an empty cell to keep colSpans aligned.
                  return (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan > 1 ? header.colSpan : undefined}
                      className={cn(
                        'p-0',
                        isBoard ? 'bg-background' : 'border-r border-table-header-border bg-table-header',
                      )}
                    />
                  );
                }

                const meta = header.column.columnDef.meta;
                const align = meta?.align ?? 'left';
                const headerAlign = meta?.headerAlign ?? align;
                const active = sort?.find((spec) => spec.field === header.column.id);
                // Opt-in, inverting TanStack's default: the export is ordered by the server, so a
                // header off the report's `sort:` whitelist (§5.4) offers an order it cannot carry.
                const sortable =
                  onSortChange !== undefined && header.column.columnDef.enableSorting === true;

                const content = flexRender(header.column.columnDef.header, header.getContext());

                // colSpan draws a group heading over its children; root leaves row-span the full
                // header height so group headings start after them.
                const rootLeafSpan = isRootLeaf && headerGroupIndex === 0 && headerRowCount > 1;
                const spanning = header.colSpan > 1;

                // Plain-string leaf titles are bounded here so a long label wraps rather than
                // running its column wide; see {@link HEADER_TITLE}. Leaves only.
                const title =
                  // A board's few columns have declared widths; the 96px wrap box only mangles them.
                  !spanning && !isBoard && typeof header.column.columnDef.header === 'string' ? (
                    <span className={HEADER_TITLE}>{content}</span>
                  ) : (
                    content
                  );
                const rowSpan = rootLeafSpan
                  ? headerRowCount
                  : header.rowSpan > 1
                    ? header.rowSpan
                    : undefined;

                return (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan > 1 ? header.colSpan : undefined}
                    rowSpan={rowSpan}
                    // Neutral surface; primary stays reserved for actions and focus. Titles wrap
                    // rather than ellipsis — truncated, "Material Cost %" and "Material Cost" are
                    // the same string. Sticky is on <thead>, not here.
                    className={cn(
                      isBoard
                        ? 'border-b border-border bg-background text-muted-foreground'
                        : 'bg-table-header text-table-header-foreground border-r border-b border-table-header-border text-xs font-semibold',
                      // Single-line headers keep columns as wide as the data; only group titles wrap.
                      spanning ? WRAP_HEADER : 'whitespace-nowrap',
                      rowSpan !== undefined && rowSpan > 1 && 'align-middle',
                      alignClass[headerAlign],
                      spanning && 'text-center',
                      meta?.headerClassName,
                    )}
                    aria-sort={
                      active === undefined
                        ? sortable
                          ? 'none'
                          : undefined
                        : active.dir === 'asc'
                          ? 'ascending'
                          : 'descending'
                    }
                  >
                    {sortable ? (
                      // Beside the header content, not wrapping it, so a header filter input stays usable.
                      <div
                        className={cn(
                          'flex items-start gap-0.5',
                          headerAlign === 'right' && 'flex-row-reverse',
                        )}
                      >
                        <div className="min-w-0 flex-1">{title}</div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          aria-label={text('Sort by {column}', { column: header.column.id })}
                          // The ghost hover would otherwise repaint the icon in body ink and lose it.
                          className="size-5 shrink-0 rounded-sm p-0 text-table-header-foreground hover:bg-table-header-foreground/10 hover:text-table-header-foreground [&_svg]:size-2.5"
                          // Measures sort worst-first: on figures the interesting end is not the alphabetical one.
                          onClick={() => toggleSort(header.column.id, align === 'right')}
                        >
                          <FontAwesomeIcon
                            icon={
                              active === undefined
                                ? faSort
                                : active.dir === 'asc'
                                  ? faSortUp
                                  : faSortDown
                            }
                            className={cn(active === undefined && 'opacity-40')}
                            aria-hidden
                          />
                        </Button>
                      </div>
                    ) : (
                      title
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody aria-busy={isLoading}>
          {isLoading ? (
            Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
              <TableRow key={`skeleton-${String(rowIndex)}`} className="hover:bg-transparent">
                {table.getVisibleLeafColumns().map((column) => (
                  <TableCell key={column.id}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : rows.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={columnCount} className="h-40 whitespace-normal p-6 align-middle">
                <div className="mx-auto flex max-w-sm flex-col items-center gap-2 text-center">
                  <FontAwesomeIcon icon={faTableList} className="size-6 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {typeof emptyMessage === 'string' ? text(emptyMessage) : emptyMessage}
                  </p>
                </div>
              </TableCell>
            </TableRow>
          ) : virtualized ? (
            <>
              {padTop > 0 ? (
                // Runtime-computed geometry — the one place §4.8 permits inline `style`.
                <TableRow aria-hidden className="hover:bg-transparent">
                  <TableCell colSpan={columnCount} className="border-0 p-0" style={{ height: padTop }} />
                </TableRow>
              ) : null}

              {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index];
                if (row === undefined) {
                  return null;
                }
                return (
                  <TableRow
                    key={row.id}
                    className={cn(
                      !isBoard && virtualRow.index % 2 === 1 && 'bg-table-row-alt',
                      'hover:bg-table-row-hover',
                    )}
                  >
                    {renderCells(row)}
                  </TableRow>
                );
              })}

              {padBottom > 0 ? (
                <TableRow aria-hidden className="hover:bg-transparent">
                  <TableCell
                    colSpan={columnCount}
                    className="border-0 p-0"
                    style={{ height: padBottom }}
                  />
                </TableRow>
              ) : null}
            </>
          ) : (
            rows.map((row, index) => (
              <TableRow
                key={row.id}
                className={cn(
                  !isBoard && index % 2 === 1 && 'bg-table-row-alt',
                  isBoard ? 'hover:bg-transparent' : 'hover:bg-table-row-hover',
                )}
              >
                {renderCells(row)}
              </TableRow>
            ))
          )}
        </TableBody>

        {/* Pinned to the bottom of the scroll box so it stays visible on a long report, and in
            <tfoot> so screen readers announce it as a summary rather than as data. */}
        {(totals !== undefined || (footerRows !== undefined && footerRows.length > 0))
          && !isLoading && rows.length > 0 ? (
          <TableFooter className="sticky bottom-0 z-10 bg-table-total font-semibold">
            {totals !== undefined ? (
              <TableRow className="hover:bg-transparent">
                {table.getVisibleLeafColumns().map((column) => {
                  const meta = column.columnDef.meta;
                  return (
                    <TableCell
                      key={column.id}
                      className={cn(
                        !isBoard && 'border-r border-table-cell-border',
                        alignClass[meta?.align ?? 'left'],
                        meta?.cellClassName,
                      )}
                    >
                      {totalsFor(column.id)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ) : null}
            {/* Lighter than the totals row: these are supporting figures, not the headline. */}
            {footerRows?.map((row) => (
              <TableRow key={row.id} className="font-normal hover:bg-transparent">
                {table.getVisibleLeafColumns().map((column) => {
                  const meta = column.columnDef.meta;
                  return (
                    <TableCell
                      key={column.id}
                      className={cn(
                        !isBoard && 'border-r border-table-cell-border',
                        alignClass[meta?.align ?? 'left'],
                      )}
                    >
                      {column.id === row.labelColumn ? row.label : row.values[column.id] ?? null}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableFooter>
        ) : null}
        </Table>
      </div>
      {/* An empty or loading result would otherwise print "0–0 of 0 · Page 0 of 0", which reads as a
          fault. `role="group"` because an `aria-label` on a bare <div> is not exposed to AT (§4.9). */}
      {data.length === 0 || isLoading || fitToScreen || !paginate ? null : (
        <div
          role="group"
          aria-label={text('Table pagination')}
          className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-xs text-muted-foreground"
        >
          {/* Nothing else on screen says whether "Total" covers the page or the whole result
              (§4.3). Withheld when the result was capped, because then so is the totals row. */}
          {totals !== undefined && multiPage && !truncated ? (
            <span className="mr-auto">Totals cover all {count.format(data.length)} rows.</span>
          ) : null}
          <label className="flex items-center gap-1.5">
            {text('Rows per page')}
            <Select
              value={String(pagination.pageSize)}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger size="sm" className="h-7 min-w-16 px-2 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          {/* "of N" claims N is everything; "N loaded" claims only what reached the browser (§4.3, §5.7). */}
          <span>
            {count.format(firstRow)}–{count.format(lastRow)} of {count.format(data.length)}
            {truncated ? ' loaded' : null}
          </span>
          <span>
            {text('Page {current} of {total}', { current: count.format(pagination.pageIndex + 1), total: count.format(pageCount) })}
          </span>
          <div className="flex items-center gap-0.5">
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={text('Previous page')}
              disabled={!table.getCanPreviousPage()}
              onClick={() => {
                table.previousPage();
              }}
            >
              <FontAwesomeIcon icon={faChevronLeft} aria-hidden />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={text('Next page')}
              disabled={!table.getCanNextPage()}
              onClick={() => {
                table.nextPage();
              }}
            >
              <FontAwesomeIcon icon={faChevronRight} aria-hidden />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
